// Ride data shapes + pure helpers. The fetch functions that use these stay per-app
// (they're coupled to each app's API config) — see apps/*/src/lib/rides.ts.

/** Summary row from GET /me/rides — mirrors the backend RideSummaryDto. No track points. */
export interface RideSummary {
  id: string;
  name: string | null;
  startedAt: string; // ISO 8601
  endedAt: string;
  distanceMeters: number;
  durationSeconds: number;
  avgSpeedMps: number;
  maxSpeedMps: number;
  maxLeanDeg: number | null;
  /** Google-encoded simplified track, for the list thumbnail / initial map fit. */
  overviewPolyline: string;
  pointCount: number;
}

/** One fix from GET /me/rides/{id}/track — mirrors the backend RidePointDto. */
export interface RideTrackPoint {
  timestamp: string;
  lat: number;
  lon: number;
  speedMps: number | null;
  headingDeg: number | null;
  // Telemetry (null when the fix had no calibrated motion). longG/leanDeg are per-interval
  // marginal peaks for the traces; ggLongG/ggLatG the co-occurring pair for the G-G.
  longG?: number | null;
  leanDeg?: number | null;
  ggLongG?: number | null;
  ggLatG?: number | null;
}

/**
 * Merges server rides with locally-recorded ones, keyed by id. A ride uploaded to the
 * server takes precedence over its local copy (the server holds the normalised summary);
 * a not-yet-uploaded local ride still shows. Sorted newest first.
 */
export function mergeRides(server: RideSummary[], local: RideSummary[]): RideSummary[] {
  const byId = new Map<string, RideSummary>();
  for (const r of local) byId.set(r.id, r);
  for (const r of server) byId.set(r.id, r); // server wins on conflict
  return [...byId.values()].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}
